import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Autocomplete,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  Box,
  Button,
  Typography,
  Paper,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useState, useEffect, useRef } from 'react';
import { casesApi } from 'src/api/casesApi';
import BulkCreateTable from './BulkCreateTable';
import toast from 'react-hot-toast';

interface Organization {
  id: string;
  name: string;
}

interface Project {
  id: string;
  name: string;
  organizationId: string;
}

interface BulkCreateDialogProps {
  open: boolean;
  onClose: (event?: {}, reason?: 'backdropClick' | 'escapeKeyDown') => void;
}

const BulkCreateDialog = ({ open, onClose }: BulkCreateDialogProps) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [sameOrgForAll, setSameOrgForAll] = useState(true);
  const [sameProjectForAll, setSameProjectForAll] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);

  const tableRef = useRef<{
    validateAllRows: () => boolean;
    getTableData: () => any[];
    addRow: () => void;
  }>(null);
  const [totalCases, setTotalCases] = useState(
    tableRef.current?.getTableData()?.length ?? 0
  );

  // Fetch organizations on component mount
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await casesApi.getOrganizationId();
        setOrganizations(response.data);
      } catch (error) {
        console.error('Error fetching organizations:', error);
      }
    };
    fetchOrganizations();
  }, []);

  // Fetch projects when organization is selected
  useEffect(() => {
    const fetchProjects = async () => {
      if (selectedOrg) {
        try {
          const response = await casesApi.getProjectsById(selectedOrg?.id);
          setProjects(response.data);
        } catch (error) {
          console.error('Error fetching projects:', error);
        }
      } else {
        setProjects([]);
      }
      setSelectedProject(null); // Reset selected project when org changes
    };
    fetchProjects();
  }, [selectedOrg]);

  // Update the switch handlers to sync values
  const handleOrgSwitchChange = (checked: boolean) => {
    setSameOrgForAll(checked);
    if (checked) {
      // When enabling "Same for all", set all rows to current selection
      setSelectedOrg(selectedOrg);
    }
  };

  const handleProjectSwitchChange = (checked: boolean) => {
    setSameProjectForAll(checked);
    if (checked) {
      // When enabling "Same for all", set all rows to current selection
      setSelectedProject(selectedProject);
    }
  };

  // Add the conversion function
  const convertToSeconds = (timeString: string) => {
    const timeUnits = timeString.match(/\d+\s*[hm]/g);
    if (!timeUnits) return 0;

    let totalSeconds = 0;

    timeUnits.forEach((unit) => {
      const number = parseInt(unit, 10);
      const timeUnit = unit.replace(/\d+\s*/g, ''); // Remove digits and spaces to get the unit

      if (timeUnit === 'h') {
        totalSeconds += number * 3600; // 1 hour = 3600 seconds
      } else if (timeUnit === 'm') {
        totalSeconds += number * 60; // 1 minute = 60 seconds
      }
    });

    return totalSeconds;
  };

  const handleSubmit = async () => {
    try {
      const isValid = tableRef.current?.validateAllRows();

      if (!isValid) {
        toast.error('Please fix the validation errors before submitting.');
        return;
      }

      setIsSubmitting(true);
      setShowProgress(true);
      setProgress(0);
      setSuccessCount(0);
      setStartTime(new Date());
      setEndTime(null);

      // Get the table data
      const cases = tableRef.current?.getTableData();
      if (!cases?.length) return;
      setTotalCases(cases.length);

      // Transform all data first
      const payload = cases.map((row) => ({
        organization: {
          id: row?.organization?.id,
          name: row?.organization?.name,
        },
        project: row.project,
        summary: row.summary,
        description: row.description,
        caseType: row.caseType,
        priority: row.priority,
        assignee: {
          id: row?.assignee?.id,
          name: row?.assignee?.name,
        },
        labels: row.label,
        expense: row.expense?.name,
        billingAccountType: { ...row.account, shiftWorkLogsToThis: false },
        timeEstimate: parseFloat(convertToSeconds(row.estimatedTime) as any),
        sprint: { id: row?.sprint?.id, name: row?.sprint?.name },
        releaseVersion: [row.fixVersion],
        module: row.module,
      }));

      // Process in chunks of 50
      const chunkSize = 50;
      const chunks = [];
      for (let i = 0; i < payload.length; i += chunkSize) {
        chunks.push(payload.slice(i, i + chunkSize));
      }

      for (let i = 0; i < chunks.length; i++) {
        // eslint-disable-next-line no-await-in-loop
        await casesApi.bulkCreateCases(chunks[i]);
        setSuccessCount((prev) => prev + chunks[i].length);
        setProgress((successCount / payload.length) * 100);
      }

      setEndTime(new Date());
      toast.success('Cases created successfully!');
      onClose();
      
      // Add delay before closing
      // setTimeout(() => {
      //   onClose();
      // }, 10000); 
    } catch (error) {
      console.error('Error creating cases:', error);
      toast.error('Failed to create cases. Please try again.');
      setEndTime(new Date());
    } finally {
      setIsSubmitting(false);
      setShowProgress(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      disableEscapeKeyDown
      sx={{
        '& .MuiDialog-paper': {
          width: '80%',
          height: '80vh',
          margin: '20px',
        },
      }}>
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 'none',
        }}>
        <Typography
          sx={{ display: 'flex', fontSize: 16, alignItems: 'center' }}
          variant="h4"
          fontWeight={500}>
          <AddIcon sx={{ marginRight: '10px' }} />
          Bulk Create Cases
        </Typography>
        <IconButton
          aria-label="close"
          onClick={() => onClose()}
          sx={{
            color: (theme) => theme.palette.secondary.main,
          }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          position: 'relative',
          height: 'calc(100% - 64px - 64px)',
          overflow: 'auto',
          borderTop: 'none',
        }}>
        <Grid container spacing={2} sx={{ mt: 0.5, mb: 2 }}>
          <Grid item xs={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 1,
                }}>
                <Typography variant="subtitle1" fontWeight={500}>
                  Organization
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      mr: 1,
                      color: 'secondary.main',
                      fontWeight: 500,
                    }}>
                    SAME FOR ALL
                  </Typography>
                  <Switch
                    size="small"
                    checked={sameOrgForAll}
                    onChange={(e) => handleOrgSwitchChange(e.target.checked)}
                    color="secondary"
                  />
                </Box>
              </Box>
              <Autocomplete
                size="small"
                options={organizations}
                value={selectedOrg}
                onChange={(_, newValue) => {
                  setSelectedOrg(newValue);
                  setSelectedProject(null);
                }}
                getOptionLabel={(option) => option?.name || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select Organization"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'background.paper',
                      },
                    }}
                  />
                )}
                disabled={!sameOrgForAll}
              />
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 1,
                }}>
                <Typography variant="subtitle1" fontWeight={500}>
                  Project
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      mr: 1,
                      color: 'secondary.main',
                      fontWeight: 500,
                    }}>
                    SAME FOR ALL
                  </Typography>
                  <Switch
                    size="small"
                    checked={sameProjectForAll}
                    onChange={(e) =>
                      handleProjectSwitchChange(e.target.checked)
                    }
                    color="secondary"
                  />
                </Box>
              </Box>
              <Autocomplete
                size="small"
                options={projects}
                value={selectedProject}
                onChange={(_, newValue) => {
                  setSelectedProject(newValue);
                }}
                getOptionLabel={(option) => option?.name || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select Project"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'background.paper',
                      },
                    }}
                  />
                )}
                disabled={!sameProjectForAll || !selectedOrg}
              />
            </Paper>
          </Grid>
        </Grid>

        <BulkCreateTable
          ref={tableRef}
          organizations={organizations}
          projects={sameOrgForAll ? projects : []}
          sameOrgForAll={sameOrgForAll}
          sameProjectForAll={sameProjectForAll}
          selectedOrg={selectedOrg}
          selectedProject={selectedProject}
        />
      </DialogContent>
      {showProgress && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                flexGrow: 1,
                height: 6,
                borderRadius: 1,
              }}
            />
          </Box>
        </Box>
      )}
      <DialogActions sx={{ p: 2, justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ minWidth: 35 }}>
          {startTime && (
            <Typography variant="body2" color="text.secondary">
              {`${successCount} of ${totalCases ?? 0} cases`}
              <Typography
                variant="caption"
                display="block"
                color="text.secondary">
                Started: {startTime?.toLocaleTimeString()}
                {endTime && ` • Ended: ${endTime?.toLocaleTimeString()}`}
              </Typography>
            </Typography>
          )}
        </Box>
        <Box>
          <Tooltip title="Add New Row" placement="top">
            <IconButton
              color="secondary"
              onClick={() => tableRef.current?.addRow()}
              aria-label="add row"
              sx={{ border: '1px solid', borderColor: 'divider' }}>
              <AddIcon />
            </IconButton>
          </Tooltip>
          <Button
            sx={{ ml: 2 }}
            variant="contained"
            color="secondary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            size="large">
            {isSubmitting ? 'Creating Cases...' : 'Create Cases'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default BulkCreateDialog;
